/**
 * Material category catalog hook.
 *
 * Wraps ``GET /api/v1/material-categories`` — the chip-filter source
 * for the landing ``ProcessPicker``. The catalog is platform-managed
 * (one row per material axis: Plech / Plast / Kov / …), edited via
 * the Platform admin ``Material Categories`` tab.
 *
 * 10-minute stale time matches ``useProcesses`` since the two are
 * rendered side-by-side and the catalog rarely changes.
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

const API = "/api/v1";

export interface MaterialCategory {
  slug: string;
  label_i18n: Record<string, string>;
  color: string;
  sort_order: number;
  /** Parent slug for the chip-filter tree; ``null``/absent = top-level root. */
  parent_slug?: string | null;
}

const materialCategoryQueryKeys = {
  all: () => ["material-categories"] as const,
} as const;

export function useMaterialCategories(
  options?: Omit<UseQueryOptions<MaterialCategory[]>, "queryKey" | "queryFn">,
) {
  return useQuery<MaterialCategory[]>({
    queryKey: materialCategoryQueryKeys.all(),
    queryFn: () =>
      apiFetch<MaterialCategory[]>(`${API}/material-categories`),
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

/**
 * Resolve the localised chip label for one material slug.
 * Falls back: requested locale → Czech → raw slug.
 */
export function pickMaterialCategoryLabel(
  category: Pick<MaterialCategory, "slug" | "label_i18n"> | undefined,
  language: string,
): string {
  if (!category) return "";
  const labels = category.label_i18n ?? {};
  return labels[language] ?? labels.cs ?? category.slug;
}
